import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';
import { useMe } from '@/features/crm/auth/use-capabilities';
import { CustomerEditPanel } from '@/features/crm/components/CustomerEditPanel';
import { avatarStyle, initialsOf } from '@/features/crm/lib/avatar';
import { NotesPanel } from '@/features/crm/components/NotesPanel';
import { SettingsPanel } from '@/features/crm/components/SettingsPanel';
import { HotelSupplierPanel } from '@/features/crm/components/HotelSupplierPanel';
import { TagsPanel } from '@/features/crm/components/TagsPanel';
import { FollowupsPanel } from '@/features/crm/components/FollowupsPanel';
import { ActivityPanel } from '@/features/crm/components/ActivityPanel';
import { ContactsPanel } from '@/features/crm/components/ContactsPanel';
import { RelationshipPanel } from '@/features/crm/components/RelationshipPanel';
import { ContractsPanel } from '@/features/crm/components/ContractsPanel';

interface CustomerProfile {
  customer_id: string;
  name: string;
  /**
   * The ONE account number, or null when the customer has none or has several.
   * `accountnumber` upstream is a comma-joined list of booking/GDS codes — the
   * API resolves it (lib/account-codes.ts there); these three fields are what
   * comes back.
   */
  account_number: string | null;
  account_codes?: string[];
  additional_account_codes?: string[];
  domain: string | null;
  website: string | null;
  status: string | null;

  cust_ae: string | null;
  cust_ae_email: string | null;
  cust_csm: string | null;
  cust_csm_email: string | null;
  cust_ops_mgr: string | null;
  cust_ops_mgr_email: string | null;
  sales_person: string | null;
  sales_person_email: string | null;

  key_contact: string | null;
  key_contact_email: string | null;
  key_contact_phone: string | null;
  key_contact_title: string | null;
  arrangers: string | null;

  industry: string | null;
  industry_group: string | null;
  sector: string | null;
  subsector: string | null;
  internal_industry: string | null;
  profit_center: string | null;
  est_annual_revenue: number | null;
  employees: number | null;
  lead_source: string | null;
  size: string | null;

  address: string | null;
  city: string | null;
  state: string | null;
  // CRM-only, editable, never overwritten by the Domo sync.
  is_global?: boolean | null;
  fee_structure?: string | null;
  has_partner_agency?: boolean | null;
  lifecycle_stage?: string | null;
  zip: string | null;
  combined_iata: string | null;
  andavo_client_iid: string | null;

  pnrs_air_90d: number | null;
  pnrs_hotel_90d: number | null;
  pnrs_car_90d: number | null;
  travelers_12m: number | null;
  total_spend_12m: number | null;
  air_spend_12m: number | null;
  hotel_spend_12m: number | null;
  car_spend_12m: number | null;
  other_spend_12m: number | null;
  latest_booking: string | null;
  ra_savings_12m: number | null;

  btp_enabled_hint: boolean | null;
  vip_usage: boolean | null;
  hotel_contract: boolean | null;
  groups_meetings: boolean | null;
  hr_feed: boolean | null;

  obt: string | null;
  concur_expense: boolean | null;
  online_pct_12m: number | null;
  vpay_vendor: string | null;
  office: string | null;
  monthly_billed: boolean | null;

  lifecycle: {
    lead: string | null;
    sales_qualified: string | null;
    opportunity: string | null;
    customer_since: string | null;
    last_modified: string | null;
  } | null;
  btp_addendum_signed: string | null;
  contract_start: string | null;
  /**
   * The governing contract end date, from the same resolver the Renewals queue
   * uses. Null when nothing is on file — which the hero must SAY, because an
   * unrecorded contract and a comfortably distant one look identical when the
   * field just disappears.
   */
  next_renewal: {
    end_date: string;
    days_out: number;
    contract_type: string;
    vendor: string | null;
    lapsed: boolean;
    contract_count: number;
  } | null;

  subcustomers: Array<{ id: string; name: string }> | null;

  health_band: string | null;
  health_score: number | null;
  spend_yoy_pct: number | null;
  pnrs_yoy_pct: number | null;
}

interface Settings {
  btpEnabled: boolean;
  hasRecord: boolean;
}

/**
 * The customer's tabs.
 *
 * WAS 'profile' | 'notes' | 'btp' | 'settings', where "Profile" held NINE
 * sections — spend mix, account team, contacts, key contact, lifecycle, tooling,
 * classification, location, relationship, contracts, tags, follow-ups AND the
 * activity timeline — as one continuous scroll. Everything past the fold was
 * effectively unfindable, and the history log sat at the very bottom of it.
 *
 * Now one tab per question someone actually arrives with:
 *   overview  — who is this account, what does it spend, who owns it
 *   contacts  — the people, and how this customer relates to others
 *   activity  — what has been DONE with them: notes, to-dos, tags, history
 *   documents — contracts and agreements
 *   settings  — configuration
 * plus the existing role-gated Hotel Supplier tab.
 */
type Tab =
  | 'overview'
  | 'contacts'
  | 'activity'
  | 'documents'
  | 'btp'
  | 'settings';

export default function CustomerProfilePage() {
  const { id: customerId = '' } = useParams<{ id: string }>();
  const { can } = useMe();
  // Same capability the API enforces on PATCH. Held by every internal WORKING
  // role — admin, manager, csm and btp_only (the Hotel Supplier Manager) — as of
  // 2026-08-11; `viewer` is deliberately excluded as the read-only role. Gating
  // the button on anything looser than the server's own check would offer an
  // action the API will 403.
  const canEdit = can('edit:customer_settings');
  const [editing, setEditing] = useState(false);
  const [customer, setCustomer] = useState<CustomerProfile | null>(null);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('overview');
  // Bumped after a save so the profile re-reads from the server. The PATCH
  // response reports which fields changed, but the profile also overlays live
  // Domo analytics — re-fetching is the only way to show the real merged result
  // rather than a locally-patched guess.
  const [reloadNonce, setReloadNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      apiFetch<{ customer: CustomerProfile }>(
        `/api/customers/${encodeURIComponent(customerId)}`,
      ),
      apiFetch<Settings>(
        `/api/customer-settings/${encodeURIComponent(customerId)}`,
      ),
    ])
      .then(([c, s]) => {
        if (!cancelled) {
          setCustomer(c.customer);
          setSettings(s);
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof ApiError ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [customerId, reloadNonce]);

  if (loading) {
    return (
      <div className="loading-state">
        <div className="loading-spinner" />
        <div className="loading-text">Loading customer…</div>
      </div>
    );
  }
  if (error || !customer) {
    return (
      <div className="page">
        <nav className="crumbs">
          <Link to="/crm/customers">
            <span className="arrow">←</span>
            Customers
          </Link>
          <span className="sep">/</span>
          <span className="current">Error</span>
        </nav>
        <div className="banner banner-error">
          {error || 'Customer not found'}
        </div>
      </div>
    );
  }

  const btpEnabled = settings?.btpEnabled ?? false;
  const tabs: Array<{ id: Tab; label: string; visible: boolean }> = [
    { id: 'overview', label: 'Overview', visible: true },
    { id: 'contacts', label: 'Contacts', visible: true },
    // "Activity" rather than "Notes": notes are one of four things on it, and the
    // question people arrive with is "what has happened with this account".
    { id: 'activity', label: 'Activity', visible: true },
    { id: 'documents', label: 'Documents', visible: true },
    { id: 'btp', label: 'Hotel Supplier', visible: can('view:supplier_tab') },
    { id: 'settings', label: 'Settings', visible: true },
  ];

  const handleExport = () => {
    // Single-row CSV: every field
    const rows: Array<[string, unknown]> = [
      ['Customer', customer.name],
      ['Customer ID', customer.customer_id],
      ['Account #', customer.account_number],
      ['Account Codes', (customer.account_codes ?? []).join('; ')],
      ['Domain', customer.domain],
      ['Status', customer.status],
      ['Account Executive', customer.cust_ae],
      ['Client Success Manager', customer.cust_csm],
      ['Operations Manager', customer.cust_ops_mgr],
      ['Sales Person', customer.sales_person],
      ['Key Contact', customer.key_contact],
      ['Key Contact Email', customer.key_contact_email],
      ['Key Contact Phone', customer.key_contact_phone],
      ['Industry', customer.industry],
      ['Sector', customer.sector],
      ['Subsector', customer.subsector],
      ['Size', customer.size],
      ['Employees', customer.employees],
      ['Est. Annual Revenue', customer.est_annual_revenue],
      ['Address', customer.address],
      ['Total Spend 12m', customer.total_spend_12m],
      ['Air Spend 12m', customer.air_spend_12m],
      ['Hotel Spend 12m', customer.hotel_spend_12m],
      ['Car Spend 12m', customer.car_spend_12m],
      ['Travelers 12m', customer.travelers_12m],
      ['Latest Booking', customer.latest_booking],
      ['RA Savings 12m', customer.ra_savings_12m],
      ['OBT', customer.obt],
      ['Concur Expense', customer.concur_expense ? 'Yes' : 'No'],
      ['Online % 12m', customer.online_pct_12m],
      ['vPay Vendor', customer.vpay_vendor],
      ['Office', customer.office],
      ['Hotel Supplier Management', btpEnabled ? 'Yes' : 'No'],
      ['Contract Start', customer.contract_start],
      ['Supplier Mgmt Addendum Signed', customer.btp_addendum_signed],
      ['Health Band', customer.health_band],
      ['Health Score', customer.health_score],
    ];
    const lines = rows.map(
      ([k, v]) => `${csvEscape(k)},${csvEscape(v == null ? '' : String(v))}`,
    );
    const blob = new Blob([lines.join('\n')], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `customer-${customer.customer_id}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="page">
      <nav className="crumbs">
        <Link to="/crm/customers">
          <span className="arrow">←</span>
          Customers
        </Link>
        <span className="sep">/</span>
        <span className="current">{customer.name}</span>
      </nav>

      {editing && canEdit && customer && (
        <CustomerEditPanel
          customerId={customer.customer_id}
          initial={{
            name: customer.name ?? null,
            accountExecutive: customer.cust_ae ?? null,
            csm: customer.cust_csm ?? null,
            opsManager: customer.cust_ops_mgr ?? null,
            industry: customer.industry ?? null,
            sizeBand: customer.size ?? null,
            domain: customer.domain ?? null,
            city: customer.city ?? null,
            state: customer.state ?? null,
            statusName: customer.status ?? null,
            keyContact: customer.key_contact ?? null,
            keyContactEmail: customer.key_contact_email ?? null,
            // is_global / fee_structure / has_partner_agency are NOT seeded:
            // the Relationship panel below owns them (audited PUT
            // /api/customer-settings/[id]). Passing them here would imply this
            // form edits them.
            lifecycleStage: customer.lifecycle_stage ?? null,
          }}
          onClose={() => setEditing(false)}
          onSaved={() => setReloadNonce((n) => n + 1)}
        />
      )}

      {/* IDENTITY HERO */}
      <div className="identity-hero">
        <div style={avatarStyle(customer.name, 64)}>
          {initialsOf(customer.name)}
        </div>
        <div className="identity-body">
          <div className="identity-name">
            {customer.name}
            <StatusDotDark status={customer.status} />
            {customer.health_band && (
              <span
                className={`health-pill on-dark ${healthSlug(customer.health_band)}`}
              >
                {customer.health_band}
                {customer.health_score != null && (
                  <span style={{ opacity: 0.7, marginLeft: 3 }}>
                    · {customer.health_score}
                  </span>
                )}
              </span>
            )}
          </div>
          <div className="identity-meta" data-testid="identity-meta">
            <span>{customer.customer_id}</span>
            {/*
              Only when there is ONE code to name. A customer with several has no
              single account number (see lib/account-codes), and a customer with
              none had this render as a bare "Acct " — the full set is in
              Location & IDs below either way.
            */}
            {customer.account_number &&
              customer.account_number !== customer.customer_id && (
                <>
                  <span className="dot" />
                  <span>Acct {customer.account_number}</span>
                </>
              )}
            {customer.domain && (
              <>
                <span className="dot" />
                <span style={{ fontFamily: 'Inter, sans-serif' }}>
                  {customer.domain}
                </span>
              </>
            )}
            {customer.contract_start && (
              <>
                <span className="dot" />
                <span style={{ fontFamily: 'Inter, sans-serif' }}>
                  Customer since {fmtDate(customer.contract_start)}
                </span>
              </>
            )}
            {/*
              WHEN THE NEXT DECISION LANDS — beside when they arrived.

              Everything else in this hero describes the past. A renewal is the
              moment an account actually leaves, and it sat only in the
              Contracts panel far below as the string "2024-01-01 → 2026-12-31":
              a record, not a prompt. The countdown is what changes what a CSM
              does today.
            */}
            <span className="dot" />
            <RenewalMeta renewal={customer.next_renewal} />
          </div>
          <div className="identity-tags">
            {btpEnabled && (
              <span className="tag tag-btp on-dark">Hotel Supplier</span>
            )}
            {customer.vip_usage && (
              <span className="tag tag-vip on-dark">VIP Usage</span>
            )}
            {customer.hotel_contract && (
              <span className="tag tag-hotel on-dark">Hotel Contract</span>
            )}
            {customer.groups_meetings && (
              <span className="tag tag-groups on-dark">Groups</span>
            )}
            {customer.hr_feed && (
              <span className="tag tag-hr on-dark">HR Feed</span>
            )}
            {customer.size && (
              <span className="tag tag-size on-dark">{customer.size}</span>
            )}
          </div>
        </div>
        <div className="identity-actions">
          {canEdit && (
            <button
              className="btn btn-outline"
              data-testid="customer-edit-open"
              onClick={() => setEditing((v) => !v)}
              title="Edit this customer's record"
              type="button"
            >
              {editing ? 'Close editor' : 'Edit'}
            </button>
          )}
          <button
            className="btn btn-outline"
            onClick={handleExport}
            title="Download CSV"
          >
            CSV
          </button>
          <button
            className="btn btn-outline"
            onClick={() => window.print()}
            title="Open print dialog"
          >
            Print
          </button>
        </div>
      </div>

      {/* SUB-NAV */}
      <nav className="subnav">
        {tabs
          .filter((t) => t.visible)
          .map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={'nav-tab' + (tab === t.id ? ' active' : '')}
              style={{ padding: '8px 14px 10px', marginBottom: 0 }}
            >
              {t.label}
            </button>
          ))}
      </nav>

      {tab === 'overview' && <OverviewTab customer={customer} />}
      {tab === 'contacts' && (
        <>
          <ContactsPanel customerId={customer.customer_id} />
          {/* Relationship belongs with the people, not with the contracts it used
              to sit beside: global/fee/entities/partner-agencies all answer "who
              else is this account connected to". */}
          <RelationshipPanel customerId={customer.customer_id} />
        </>
      )}
      {tab === 'activity' && (
        <>
          {/* Ordered by how it is used: write a note, see what is owed, then read
              the history. Tags sit here because tagging IS an action taken on the
              customer and shows up in the log below. */}
          <NotesPanel
            customerId={customer.customer_id}
            customerName={customer.name}
          />
          <div className="row-2">
            <FollowupsPanel customerId={customer.customer_id} />
            <TagsPanel customerId={customer.customer_id} />
          </div>
          <ActivityPanel customerId={customer.customer_id} />
        </>
      )}
      {tab === 'documents' && (
        <ContractsPanel customerId={customer.customer_id} />
      )}
      {tab === 'btp' && (
        <HotelSupplierPanel customerId={customer.customer_id} />
      )}
      {tab === 'settings' && (
        <SettingsPanel customerId={customer.customer_id} />
      )}
    </div>
  );
}

// ─── Overview tab ──────────────────────────────────────────────────────

/**
 * The read-only picture of the account: spend, owners, lifecycle, tooling,
 * classification, location.
 *
 * Was `ProfileTab` and also carried contacts, relationship, contracts, tags,
 * follow-ups and the activity timeline — thirteen sections in one scroll. Those
 * moved to the Contacts / Activity / Documents tabs; what is left is only the
 * facts you want at a glance, which is what makes this tab a landing rather than
 * a haystack.
 */
function OverviewTab({ customer }: { customer: CustomerProfile }) {
  const total = num(customer.total_spend_12m);
  const air = num(customer.air_spend_12m);
  const hotel = num(customer.hotel_spend_12m);
  const car = num(customer.car_spend_12m);
  const other = num(customer.other_spend_12m);
  const denom = Math.max(total, air + hotel + car + other, 1);

  const segments = [
    { key: 'air' as const, label: 'Air', value: air },
    { key: 'hotel' as const, label: 'Hotel', value: hotel },
    { key: 'car' as const, label: 'Car', value: car },
    { key: 'other' as const, label: 'Other', value: other },
  ];

  return (
    <>
      {/* Travel snapshot KPIs (6-card row) */}
      <div className="kpi-row kpi-row-6">
        <div className="kpi-card">
          <span className="kpi-label">Air PNRs · 90d</span>
          <span className="kpi-value kpi-value-sm">
            {fmtNumOrDash(customer.pnrs_air_90d)}
          </span>
          <span className="kpi-sub">
            {yoyChip(customer.pnrs_yoy_pct)} vs prior 90d
          </span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Hotel PNRs · 90d</span>
          <span className="kpi-value kpi-value-sm">
            {fmtNumOrDash(customer.pnrs_hotel_90d)}
          </span>
          <span className="kpi-sub">
            {customer.hotel_contract == null
              ? '—'
              : customer.hotel_contract
                ? 'Has contract'
                : 'No contract'}
          </span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Car PNRs · 90d</span>
          <span className="kpi-value kpi-value-sm">
            {fmtNumOrDash(customer.pnrs_car_90d)}
          </span>
          <span className="kpi-sub">last 90 days</span>
        </div>
        <div className="kpi-card kpi-highlight">
          <span className="kpi-label">Spend · 12mo</span>
          <span className="kpi-value kpi-value-sm">{fmtMoneyShort(total)}</span>
          <span className="kpi-sub">
            {yoyChip(customer.spend_yoy_pct)} year-over-year
          </span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Travelers · 12mo</span>
          <span className="kpi-value kpi-value-sm">
            {fmtNumOrDash(customer.travelers_12m)}
          </span>
          <span className="kpi-sub">unique in 12 months</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">RA Savings · 12mo</span>
          <span
            className="kpi-value kpi-value-sm"
            style={{
              color:
                num(customer.ra_savings_12m) > 0
                  ? 'var(--green-strong)'
                  : 'var(--text-muted)',
            }}
          >
            {fmtMoneyOrDash(customer.ra_savings_12m)}
          </span>
          <span className="kpi-sub">
            Latest booking{' '}
            {customer.latest_booking ? (
              <strong>{fmtRelativeDate(customer.latest_booking)}</strong>
            ) : (
              '—'
            )}
          </span>
        </div>
      </div>

      {/* Spend mix bar + legend */}
      <div className="section">
        <div className="section-header terra">
          <span className="section-label">Spend mix · 12 months</span>
          <span className="section-meta">Total {fmtMoney(total)}</span>
        </div>
        <div className="section-body">
          {total === 0 ? (
            <div className="empty-state">
              <div className="glyph">○</div>
              <div className="empty-head">No spend in the last 12 months</div>
              <div className="empty-sub">
                This customer hasn&rsquo;t transacted recently. They may be
                paused or onboarding.
              </div>
            </div>
          ) : (
            <>
              <div className="spend-mix-bar" title={`Total ${fmtMoney(total)}`}>
                {segments.map((s) => (
                  <div
                    key={s.key}
                    className={`spend-mix-seg ${s.key}`}
                    style={{ width: `${(s.value / denom) * 100}%` }}
                    title={`${s.label}: ${fmtMoney(s.value)} (${pct(s.value, total)})`}
                  />
                ))}
              </div>
              <div className="spend-mix-legend">
                {segments.map((s) => (
                  <div key={s.key} className={`seg-cell ${s.key}`}>
                    <div className="seg-label">{s.label}</div>
                    <span className="seg-amt">{fmtMoneyShort(s.value)}</span>
                    <span className="seg-pct">
                      {pct(s.value, total)} of program
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="row-2">
        {/* Account Team */}
        <div className="section">
          <div className="section-header">
            <span className="section-label">Account Team</span>
          </div>
          <div className="section-body">
            <div className="team-grid">
              <TeamCard
                role="Account Executive"
                name={customer.cust_ae}
                email={customer.cust_ae_email}
              />
              <TeamCard
                role="Client Success Manager"
                name={customer.cust_csm}
                email={customer.cust_csm_email}
              />
              <TeamCard
                role="Operations Manager"
                name={customer.cust_ops_mgr}
                email={customer.cust_ops_mgr_email}
              />
              <TeamCard
                role="Sales Person"
                name={customer.sales_person}
                email={customer.sales_person_email}
              />
            </div>
          </div>
        </div>

        {/* Key Contact (from Customer Master / Domo — legacy reference) */}
        <div className="section">
          <div className="section-header">
            <span className="section-label">
              Customer Master contact (Domo)
            </span>
          </div>
          <div className="section-body">
            {customer.key_contact ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  marginBottom: customer.arrangers ? 14 : 0,
                }}
              >
                <div style={avatarStyle(customer.key_contact, 44)}>
                  {initialsOf(customer.key_contact)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 'var(--fs-md)',
                      fontWeight: 600,
                      color: 'var(--text-primary)',
                    }}
                  >
                    {customer.key_contact}
                  </div>
                  {customer.key_contact_title && (
                    <div
                      style={{
                        fontSize: 'var(--fs-sm)',
                        color: 'var(--text-secondary)',
                        marginTop: 1,
                      }}
                    >
                      {customer.key_contact_title}
                    </div>
                  )}
                  <div
                    style={{
                      fontSize: 'var(--fs-base)',
                      marginTop: 4,
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 12,
                    }}
                  >
                    {customer.key_contact_email && (
                      <a
                        className="link"
                        href={`mailto:${customer.key_contact_email}`}
                      >
                        {customer.key_contact_email}
                      </a>
                    )}
                    {customer.key_contact_phone && (
                      <a
                        className="link"
                        href={`tel:${customer.key_contact_phone.replace(/[^+0-9]/g, '')}`}
                      >
                        {customer.key_contact_phone}
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div
                style={{
                  fontSize: 'var(--fs-base)',
                  color: 'var(--text-muted)',
                  fontStyle: 'italic',
                }}
              >
                No primary contact on file in Customer Master.
              </div>
            )}
            {customer.arrangers && (
              <div
                style={{
                  borderTop: '1px solid var(--border-light)',
                  paddingTop: 12,
                  marginTop: 4,
                }}
              >
                <div
                  style={{
                    fontSize: 'var(--fs-xs)',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    color: 'var(--text-muted)',
                    marginBottom: 6,
                  }}
                >
                  Arrangers
                </div>
                <pre
                  style={{
                    fontFamily: 'inherit',
                    fontSize: 'var(--fs-base)',
                    color: 'var(--text-secondary)',
                    margin: 0,
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.55,
                  }}
                >
                  {customer.arrangers}
                </pre>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Lifecycle */}
      <div className="section">
        <div className="section-header">
          <span className="section-label">Sales lifecycle</span>
          {customer.lifecycle?.last_modified && (
            <span className="section-meta">
              Last modified {fmtRelativeDate(customer.lifecycle.last_modified)}
            </span>
          )}
        </div>
        <div className="section-body">
          <Lifecycle
            stages={[
              ['Lead', customer.lifecycle?.lead ?? null],
              ['Sales Qualified', customer.lifecycle?.sales_qualified ?? null],
              ['Opportunity', customer.lifecycle?.opportunity ?? null],
              ['Customer', customer.lifecycle?.customer_since ?? null],
              ['Last Modified', customer.lifecycle?.last_modified ?? null],
            ]}
          />
        </div>
      </div>

      {/* Online & Tooling */}
      <div className="section">
        <div className="section-header terra">
          <span className="section-label">Online & Tooling</span>
        </div>
        <div className="section-body">
          <div className="tooling-grid">
            <ToolingCell
              label="Online Booking Tool"
              value={customer.obt}
              variant={customer.obt ? 'yes' : 'no'}
            />
            <ToolingCell
              label="Concur Expense"
              value={customer.concur_expense ? 'Yes' : 'No'}
              variant={customer.concur_expense ? 'yes' : 'no'}
            />
            <ToolingCell
              label="Online · 12mo"
              value={
                customer.online_pct_12m == null ? (
                  '—'
                ) : (
                  <span>
                    <span className="pct">
                      {Math.round(customer.online_pct_12m)}
                    </span>
                    %
                  </span>
                )
              }
              variant={
                customer.online_pct_12m != null && customer.online_pct_12m >= 70
                  ? 'yes'
                  : 'no'
              }
              sub={
                customer.online_pct_12m != null && customer.online_pct_12m >= 70
                  ? 'On target (≥70%)'
                  : 'Below 70% target'
              }
            />
            <ToolingCell
              label="vPay Vendor"
              value={customer.vpay_vendor || '—'}
              variant={customer.vpay_vendor ? 'yes' : 'no'}
            />
            <ToolingCell
              label="Office"
              value={customer.office || '—'}
              variant="neutral"
            />
            <ToolingCell
              label="Monthly billed"
              value={customer.monthly_billed ? 'Yes' : 'No'}
              variant={customer.monthly_billed ? 'yes' : 'no'}
            />
          </div>
        </div>
      </div>

      <div className="row-2">
        {/* Classification */}
        <div className="section">
          <div className="section-header">
            <span className="section-label">Classification</span>
          </div>
          <div className="section-body">
            <KvGrid
              rows={[
                ['Industry', customer.industry],
                ['Industry Group', customer.industry_group],
                ['Sector', customer.sector],
                ['Subsector', customer.subsector],
                ['Internal Industry', customer.internal_industry],
                ['Profit Center', customer.profit_center],
                [
                  'Est. Annual Revenue',
                  customer.est_annual_revenue == null
                    ? null
                    : fmtMoney(customer.est_annual_revenue),
                  'num',
                ],
                [
                  'Employees',
                  customer.employees == null
                    ? null
                    : customer.employees.toLocaleString(),
                  'num',
                ],
                ['Lead Source', customer.lead_source],
              ]}
            />
          </div>
        </div>

        {/* Location & IDs */}
        <div className="section">
          <div className="section-header">
            <span className="section-label">Location & IDs</span>
          </div>
          <div className="section-body">
            <KvGrid
              rows={[
                ['Customer ID', customer.customer_id],
                [
                  (customer.account_codes?.length ?? 0) === 1
                    ? 'Account code'
                    : 'Account codes',
                  // null (not an empty component) so KvGrid renders its own
                  // em-dash for the 643 customers that have no code at all.
                  customer.account_codes?.length ? (
                    <AccountCodes codes={customer.account_codes} />
                  ) : null,
                ],
                ['Andavo Client IID', customer.andavo_client_iid],
                ['Combined IATA', customer.combined_iata],
                ['Address', customer.address],
                [
                  'Website',
                  customer.website ? (
                    <a
                      href={customer.website}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {customer.website.replace(/^https?:\/\//, '')}
                    </a>
                  ) : null,
                ],
                ['Domain', customer.domain],
                [
                  'Contract Start',
                  customer.contract_start
                    ? fmtDate(customer.contract_start)
                    : null,
                ],
                [
                  'Supplier Mgmt Addendum Signed',
                  customer.btp_addendum_signed
                    ? fmtDate(customer.btp_addendum_signed)
                    : null,
                ],
              ]}
            />
          </div>
        </div>
      </div>

      {/* Subcustomers (only if present) */}
      {customer.subcustomers && customer.subcustomers.length > 0 && (
        <div className="section">
          <div className="section-header">
            <span className="section-label">Subcustomers</span>
            <span className="section-meta">{customer.subcustomers.length}</span>
          </div>
          <div className="section-body">
            <div className="sub-list">
              {customer.subcustomers.map((s) => (
                <div className="sub-row" key={s.id}>
                  <span className="sub-name">{s.name}</span>
                  <span className="sub-id">{s.id}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── small components ─────────────────────────────────────────────────

function TeamCard({
  role,
  name,
  email,
}: {
  role: string;
  name: string | null;
  email: string | null;
}) {
  if (!name) {
    return (
      <div className="team-card empty">
        <div
          style={{
            ...avatarStyle('—', 36),
            background: 'var(--border-light)',
            color: 'var(--text-faint)',
          }}
        >
          ·
        </div>
        <div style={{ flex: 1 }}>
          <div className="team-role">{role}</div>
          <div className="team-name">Not assigned</div>
        </div>
      </div>
    );
  }
  return (
    <div className="team-card">
      <div style={avatarStyle(name, 36)}>{initialsOf(name)}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="team-role">{role}</div>
        <div className="team-name">{name}</div>
      </div>
      {email && (
        <a
          className="team-action"
          href={`mailto:${email}`}
          title={email}
          aria-label={`Email ${name}`}
        >
          <svg
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
            <polyline points="22,6 12,13 2,6" />
          </svg>
        </a>
      )}
    </div>
  );
}

function StatusDotDark({ status }: { status: string | null }) {
  const map: Record<string, string> = {
    Active: 'status-dot on-dark status-active',
    Inactive: 'status-dot on-dark status-inactive',
  };
  const cls = (status && map[status]) || 'status-dot on-dark status-unknown';
  return <span className={cls}>{status || 'Unknown'}</span>;
}

function ToolingCell({
  label,
  value,
  variant,
  sub,
}: {
  label: string;
  value: React.ReactNode;
  variant: 'yes' | 'no' | 'neutral';
  sub?: string;
}) {
  const cls =
    variant === 'yes'
      ? 'tooling-value yes'
      : variant === 'no'
        ? 'tooling-value no'
        : 'tooling-value';
  return (
    <div className="tooling-cell">
      <div className="tooling-label">{label}</div>
      <span className={cls}>{value || '—'}</span>
      {sub && <div className="tooling-sub">{sub}</div>}
    </div>
  );
}

/**
 * A customer's booking/GDS account codes.
 *
 * One chip each, wrapping, rather than the joined string the field arrives as.
 * Western Washington University has 43 of them and the raw value is 1,024
 * characters — as one line of text that is not a list anyone can read, and it
 * pushed every other row of this grid off the screen.
 */
function AccountCodes({ codes }: { codes: string[] }) {
  return (
    <span className="code-chips" data-testid="account-codes">
      {codes.map((code) => (
        <code className="code-chip" key={code}>
          {code}
        </code>
      ))}
    </span>
  );
}

function KvGrid({ rows }: { rows: Array<[string, React.ReactNode, 'num'?]> }) {
  return (
    <dl className="kv-grid">
      {rows.map(([label, value, kind], i) => (
        <div className="kv-row" key={i}>
          <dt className="kv-label">{label}</dt>
          <dd
            className={
              'kv-value' +
              (value == null || value === '' ? ' empty' : '') +
              (kind === 'num' ? ' num' : '')
            }
          >
            {value == null || value === '' ? '—' : value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function Lifecycle({ stages }: { stages: Array<[string, string | null]> }) {
  // Mark the last reached stage as "current"
  const reachedIdx = stages.map(([, d]) => Boolean(d));
  const lastReached = reachedIdx.lastIndexOf(true);
  return (
    <div className="lifecycle">
      {stages.map(([label, date], i) => {
        const reached = Boolean(date);
        const current = reached && i === lastReached;
        return (
          <div
            key={label}
            className={
              'lifecycle-stage' +
              (reached ? ' reached' : '') +
              (current ? ' current' : '')
            }
          >
            <div className="stage-label">{label}</div>
            <div className="stage-date">
              {date ? fmtDate(date) : 'Not reached'}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── format helpers ───────────────────────────────────────────────────

function num(v: unknown): number {
  if (v == null) return 0;
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return isFinite(n) ? n : 0;
}

/**
 * A missing number is not zero. `fmtNum`/`fmtMoneyShort` coerce null to 0 and
 * print "0" / "$0", which reads as a measured value — the CHG overview showed
 * "0" air PNRs for a customer with 9,381 of them, and "$0" RA savings for a
 * figure nothing has ever computed. These two keep the distinction on the KPI
 * tiles; the arithmetic helpers below still coerce, because a missing addend in
 * the spend mix really is zero.
 */
function fmtNumOrDash(v: number | null | undefined): string {
  if (v == null) return '—';
  return v.toLocaleString('en-US');
}

function fmtMoneyOrDash(v: number | null | undefined): string {
  if (v == null) return '—';
  return fmtMoneyShort(v);
}

function fmtMoney(v: number): string {
  if (!isFinite(v) || v === 0) return '$0';
  return '$' + v.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function fmtMoneyShort(v: number | null): string {
  if (v == null || !isFinite(v) || v === 0) return '$0';
  if (v >= 1_000_000_000) return '$' + (v / 1_000_000_000).toFixed(2) + 'B';
  if (v >= 1_000_000) return '$' + (v / 1_000_000).toFixed(2) + 'M';
  if (v >= 10_000) return '$' + (v / 1_000).toFixed(0) + 'k';
  return '$' + v.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function fmtRelativeDate(iso: string): string {
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return fmtDate(iso);
}

function pct(v: number, total: number): string {
  if (!total) return '—';
  return ((v / total) * 100).toFixed(1) + '%';
}

function yoyChip(v: number | null): React.ReactNode {
  // null means we have no prior window to compare against — say so. Printing
  // "0.0%" for it claims the business was flat, which is a different statement.
  if (v == null)
    return <span style={{ color: 'var(--text-muted)' }}>no comparison</span>;
  if (v === 0) return <span style={{ color: 'var(--text-muted)' }}>0.0%</span>;
  const positive = v > 0;
  return (
    <span
      style={{
        color: positive ? 'var(--green-strong)' : 'var(--red-strong)',
        fontWeight: 700,
      }}
    >
      {positive ? '▲' : '▼'} {Math.abs(v).toFixed(1)}%
    </span>
  );
}

/**
 * The hero's renewal line.
 *
 * ⛔ Renders SOMETHING in every case, including "No contract on file". A field
 * that vanishes when the data is missing makes "we never recorded it" look
 * identical to "nothing is due for years", and those call for opposite actions.
 */
function RenewalMeta({
  renewal,
}: {
  renewal: CustomerProfile['next_renewal'];
}): React.ReactElement {
  if (!renewal) {
    return (
      <span data-testid="hero-renewal" style={{ opacity: 0.65 }}>
        No contract on file
      </span>
    );
  }
  if (renewal.lapsed) {
    return (
      <span data-testid="hero-renewal">
        Contract lapsed {Math.abs(renewal.days_out)} days ago
      </span>
    );
  }
  // Urgency in words first; the emphasis is reinforcement, not the carrier.
  const urgent = renewal.days_out <= 60;
  return (
    <span data-testid="hero-renewal" style={{ fontWeight: urgent ? 700 : 400 }}>
      Renews in {renewal.days_out} days
      <span style={{ opacity: 0.65 }}> · {fmtDate(renewal.end_date)}</span>
    </span>
  );
}

function healthSlug(band: string): string {
  return band.toLowerCase().replace(/\s+/g, '_');
}

function csvEscape(s: string): string {
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return '"' + s.replace(/"/g, '""') + '"';
  }
  return s;
}
